<template>
    <div ref="container" class="vs-chart-container" :style="reactiveContainerStyle">
        <svg v-if="draw" class="vs-chart-svg" :style="reactiveSvgStyle" @mouseout="handlerMouseOut">
            <g>
                <defs>
                    <filter id="path-shadow" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceAlpha" dx="2" dy="0"/>
                        <feBlend in="SourceGraphic" mode="normal"/>
                    </filter>
                </defs>
                <path v-for="(path, index) in pathData" :key="`path-${index}`"
                    :class="{
                        'mouse-hover-bright': hoverBright && index === hoverIndex && pathData.length > 1,
                        'mouse-hover-animation': animation && index === hoverIndex && pathData.length > 1,
                        'mouse-out-animation': animation && index === outIndex && index !== hoverIndex
                    }"
                    style="cursor:pointer"
                    :d="path.d"
                    :fill="colorScale ? colorScale(path.value) : color[index] || defaultColor"
                    :filter="shadowFilter ? 'url(#path-shadow)' : undefined"
                    @mouseover="handlerMouseover($event, path, index)"
                    @click="handlerClick($event, path, index)"
                >
                </path>
                <text v-for="(path, index) in pathData" :key="`path-label-${index}`"
                    :x="path.x - boundary.padding"
                    :y="path.y - boundary.padding"
                    :fill="labelColor"
                >
                    <slot name="path-label" :item="{ ...path, index }"></slot>
                </text>
                <slot name="center-label" v-if="$scopedSlots['center-label'] && hoverIndex > -1" :item="{ ...pathData[hoverIndex], boundary, index: hoverIndex }"></slot>
                <text v-if="centerLabel"
                    :x="boundary.maxX / 2"
                    :y="boundary.maxY / 2"
                    stroke="none"
                    font-size="2rem"
                    style="text-anchor:middle"
                    :fill="labelColor"
                >
                    {{ pathData[hoverIndex] && pathData[hoverIndex].label }}
                </text>
            </g>
        </svg>
    </div>
</template>

<script>
import { genPiePath } from '../helper/path'
import commonProps from '../mixins/commonProps'
export default {
    name: 'vs-svg-pie',
    mixins: [
        commonProps
    ],
    props: {
        defaultColor: {
            type: String,
            default: '#fff'
        },
        labelName: {
            type: String,
            default: 'label'
        },
        valueName: {
            type: String,
            default: 'value'
        },
        radiusIn: {
            type: Number
        },
        color: {
            type: Array,
            default: function () {
                return ['#F15F5F', '#F29661', '#F2CB61', '#E5D85C', '#BCE55C', '#86E57F', '#5CD1E5', '#6799FF', '#6B66FF', '#A566FF', '#F361DC', '#F361A6', '#8C8C8C', '#6B66FF', '#F361A6']
            }
        },
        hoverBright: {
            type: Boolean,
            default: true
        },
        animation: {
            type: Boolean,
            default: true
        },
        defaultHover: {
            type: Boolean,
            default: false
        },
        centerLabel: {
            type: Boolean,
            default: false
        },
        shadowFilter: Boolean,
        colorScale: Function
    },
    data () {
        return {
            draw: true,
            hoverIndex: -1,
            outIndex: -1,
            widthValue: undefined,
            pathData: [],
            defaultObject: {}
        }
    },
    computed: {
        reactiveContainerStyle () {
            return {
                'padding-top': this.height && this.width && this.draw ? `${this.height / this.width * (this.width / this.widthValue * 100)}%` : undefined
            }
        },
        reactiveSvgStyle () {
            return {
                height: `${this.height}px`,
                width: `${this.width / this.widthValue * 100}%`
            }
        },
        reducer () {
            return (acc, cur) => acc + cur
        },
        sumData () {
            return this.valueArray.reduce(this.reducer, 0)
        },
        valueArray () {
            return this.data.map(value => value[this.valueName])
        },
        circleData () {
            const data = []
            this.data.reduce((accumulator, currentValue) => {
                const startAngle = (accumulator / this.sumData * 360) - 90
                const endAngle = currentValue[this.valueName] / this.sumData * 360 + startAngle
                const accumulate = accumulator + currentValue[this.valueName]
                data.push({
                    label: currentValue[this.labelName],
                    value: currentValue[this.valueName],
                    startAngle,
                    endAngle,
                    accumulate
                })
                return accumulate
            }, 0)
            return data
        },
        radius () {
            return (Math.min.apply(null, [this.widthValue, this.boundary.maxY]) - this.boundary.padding) / 2
        },
        boundary () {
            const { padding, width, height, marginLeft } = this
            return {
                minX: padding + marginLeft,
                minY: padding,
                maxX: width,
                maxY: height - padding,
                padding,
                marginLeft
            }
        }
    },
    created () {
        if (this.defaultHover) {
            this.hoverIndex = 0
        }
    },
    mounted () {
        this.$nextTick(() => {
            if (this.width && this.height) {
                this.makeDrawData()
            }
            window.addEventListener('resize', this.makeDrawData)
        })
    },
    beforeUpdate () {
        this.$el.querySelectorAll('animate').forEach(el => el.remove())
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.makeDrawData)
    },
    methods: {
        makeDrawData () {
            if (this.data.length === 0) {
                this.draw = false
                return
            }
            this.draw = true
            this.pathData = this.circleData.map(item => {
                this.widthValue = this.$refs.container.getBoundingClientRect().width
                const { startAngle, endAngle } = item
                const pathData = genPiePath(startAngle, endAngle, this.radius * this.radiusIn, this.radius, this.boundary)
                return {
                    ...item,
                    ...pathData
                }
            })
        },
        handlerMouseover (event, data, index) {
            this.hoverIndex = index
            this.$emit('mouseover:point', { event, data })
        },
        handlerClick (event, data, index) {
            this.$emit('click:point', { event, data })
        },
        handlerMouseOut () {
            if (!this.defaultHover) {
                this.hoverIndex = -1
            }
        }
    },
    watch: {
        data: {
            handler (value) {
                if (!value) return
                this.makeDrawData()
            },
            deep: true
        },
        boundary: {
            handler (value) {
                if (!value) return
                this.makeDrawData()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
.vs-chart-container {
    height: 0;
    position: relative;
    .vs-chart-svg {
        position: absolute;
        top: 0;
        left: 0;
    }
}
.mouse-hover-bright {
    opacity: 0.8
}
.mouse-hover-animation {
    transform: scale(1.05);
    transform-origin: center;
    animation: mousehover 0.3s ease-in-out both;
    -webkit-animation: mousehover 0.3s ease-in-out both;
}
@keyframes mousehover {
    from {
        transform: scale(1);
        transform-origin: center center;
        -webkit-transform: scale(1);
        -webkit-transform-origin: center center;
        -webkit-animation-timing-function: ease-out;
        animation-timing-function: ease-out;
    }
    50% {
        transform: scale(1);
        -webkit-transform: scale(1);
        -webkit-animation-timing-function: ease-out;
        animation-timing-function: ease-out;
    }
}

.mouse-out-animation {
    transform: scale(1);
    transform-origin: center;
    animation: mouseout 0.3s ease-in-out both;
    -webkit-animation: mouseout 0.3s ease-in-out both;
}
@keyframes mouseout {
    from {
        transform: scale(1.05);
        transform-origin: center center;
        -webkit-transform: scale(1.05);
        -webkit-transform-origin: center center;
        -webkit-animation-timing-function: ease-out;
        animation-timing-function: ease-out;
    }
}
</style>
